import { Component, inject, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CpaasAuthService } from '../document-processing/cpaas-auth.service';
import { CpaasClientsService } from '../document-processing/cpaas-clients.service';
import { CdrProcessingService } from '../document-processing/cdr-processing.service';
import { CpaasClient } from '../document-processing/cdr-clients.constants';

const API_BASE = '/cpaas-telekom/api/document-store';

const CATEGORIES = ['WHOLESALE_BILL', 'ENTERPRISE_BILL', 'ENTERPRISE_CDR', 'CONTRACTS', 'USE_CASES', 'OTHERS'];

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

@Component({
  selector: 'app-cdr-billing',
  templateUrl: './cdr-billing.component.html',
  styles: [`
    .token-ok    { color: #16a34a; font-weight: 600; }
    .token-warn  { color: #d97706; font-weight: 600; }
    .token-error { color: #dc2626; font-weight: 600; }
    pre { white-space: pre-wrap; word-break: break-all; font-size: 0.75rem; }
  `]
})
export class CdrBillingComponent implements OnInit {
  private http       = inject(HttpClient);
  auth               = inject(CpaasAuthService);
  private clientsSvc = inject(CpaasClientsService);
  private cdrSvc     = inject(CdrProcessingService);

  loading = false;

  // Auth form
  username = '';
  password = '';
  otp      = '';

  // Billing params
  clientOptions  : CpaasClient[] = [];
  selectedClient : CpaasClient | null = null;

  readonly months = MONTH_NAMES.map((label, i) => ({ value: i + 1, label }));
  readonly years  : number[] = [];
  selectedMonth   : number;
  selectedYear    : number;

  pricePerSms = 0.02;
  category    = 'ENTERPRISE_CDR';
  readonly categories = CATEGORIES;

  // Log
  logMessages : string[] = [];
  lastError   = '';

  // Delegating getters — same pattern as document-store
  get accessToken()      { return this.auth.accessToken; }
  get tokenStatusClass() { return this.auth.tokenStatusClass; }
  get tokenStatusLabel() { return this.auth.tokenStatusLabel; }
  get logText()          { return this.logMessages.join('\n'); }

  constructor() {
    const now = new Date();
    this.selectedMonth = now.getMonth() + 1;
    this.selectedYear  = now.getFullYear();
    for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) {
      this.years.push(y);
    }
  }

  ngOnInit() {
    this.clientsSvc.getClients().subscribe(clients => {
      this.clientOptions = clients;
      if (clients.length > 0) this.selectedClient = clients[0];
    });
  }

  // ── Auth ──────────────────────────────────────────────────────────────────

  async login() {
    if (!this.username || !this.password) {
      this.lastError = 'Introduce usuario y contraseña.';
      return;
    }
    this.loading   = true;
    this.lastError = '';
    try {
      await this.auth.login(this.username, this.password, this.otp || undefined);
    } catch (err: any) {
      this.lastError = JSON.stringify(err?.error ?? err?.message ?? err, null, 2);
    } finally {
      this.loading = false;
    }
  }

  async refreshAccessToken() {
    this.loading   = true;
    this.lastError = '';
    try {
      await this.auth.refreshAccessToken();
    } catch (err: any) {
      this.lastError = JSON.stringify(err?.error ?? err?.message ?? err, null, 2);
    } finally {
      this.loading = false;
    }
  }

  clearSession() {
    this.auth.clearSession();
    this.logMessages = [];
    this.lastError   = '';
  }

  // ── Billing ───────────────────────────────────────────────────────────────

  async generate() {
    if (!this.selectedClient) {
      this.lastError = 'Selecciona un cliente.';
      return;
    }
    const tokenOk = await this.auth.ensureValidToken();
    if (!tokenOk) {
      this.lastError = 'No hay sesión activa o ha expirado. Inicia sesión primero.';
      return;
    }

    this.loading     = true;
    this.lastError   = '';
    this.logMessages = [];

    try {
      const mm        = String(this.selectedMonth).padStart(2, '0');
      const daysInMonth = new Date(this.selectedYear, this.selectedMonth, 0).getDate();
      const dateFrom  = `${this.selectedYear}-${mm}-01T00:00:00Z`;
      const dateTo    = `${this.selectedYear}-${mm}-${String(daysInMonth).padStart(2, '0')}T23:59:59Z`;

      this.log(`Cliente:   ${this.selectedClient.label}`);
      this.log(`Período:   ${MONTH_NAMES[this.selectedMonth - 1]} ${this.selectedYear}`);
      this.log(`Categoría: ${this.category}`);
      this.log('─'.repeat(50));
      this.log(`🔍 Buscando ficheros en Document Store...`);

      // Fetch all pages
      const allFiles: any[] = [];
      let page         = 0;
      let totalElements = Infinity;

      while (allFiles.length < totalElements) {
        const params = new HttpParams()
          .set('wholesaleCustomerIds', this.selectedClient.wholesaleCustomerId)
          .set('category',             this.category)
          .set('dateCreatedFrom',      dateFrom)
          .set('dateCreatedTo',        dateTo)
          .set('page',                 page.toString())
          .set('pageSize',             '100')
          .set('orderBy',              'dateCreated')
          .set('orderDirection',       'ASC');

        const headers  = new HttpHeaders({ 'Authorization': `Bearer ${this.auth.accessToken}` });
        const response = await firstValueFrom(
          this.http.get<any>(`${API_BASE}/v2/files`, { headers, params })
        );

        const items: any[] = Array.isArray(response) ? response
          : (response?.content ?? response?.files ?? response?.items ?? []);
        totalElements = response?.totalElements ?? response?.total ?? response?.totalCount ?? items.length;
        allFiles.push(...items);

        if (items.length < 100) break;
        page++;
      }

      this.log(`📄 Encontrados: ${allFiles.length} fichero(s)`);

      if (allFiles.length === 0) {
        this.log(`\n⚠️ No se encontraron ficheros para los filtros seleccionados.`);
        return;
      }

      let allRows: any[] = [];

      for (const file of allFiles) {
        const fileId   = file.id ?? file.fileId ?? file.uuid;
        const fileName = file.name ?? file.filename ?? file.fileName ?? fileId;
        this.log(`\n📥 Descargando: ${fileName}`);

        const headers = new HttpHeaders({ 'Authorization': `Bearer ${this.auth.accessToken}` });
        const blob    = await firstValueFrom(
          this.http.get(`${API_BASE}/files/${fileId}/file`, { headers, responseType: 'blob' })
        );

        const result = await this.cdrSvc.processBlob(blob, this.pricePerSms);
        result.log.forEach(l => this.log(l));
        allRows = allRows.concat(result.rows);
      }

      this.log('\n' + '─'.repeat(50));
      this.log(`RESUMEN:`);
      this.log(`   Total filas procesadas: ${allRows.length}`);

      if (allRows.length > 0) {
        const rowsWithTotal = this.cdrSvc.addTotalRow(allRows, this.pricePerSms);
        const filename      = `CDR_${this.selectedClient.code}_${this.selectedYear}-${mm}`;
        this.cdrSvc.generateXlsx(rowsWithTotal, filename);
        const totalSegments = allRows.reduce((s, r) => s + (typeof r['Segmentos'] === 'number' ? r['Segmentos'] : 0), 0);
        this.log(`   Total segmentos: ${totalSegments}`);
        this.log(`   Total precio:    ${(totalSegments * this.pricePerSms).toFixed(5)}`);
        this.log(`\n✅ Fichero generado: ${filename}.xlsx`);
      } else {
        this.log(`\n⚠️ No se encontraron filas válidas para generar el fichero.`);
      }
    } catch (err: any) {
      this.lastError = JSON.stringify(err?.error ?? err?.message ?? err, null, 2);
      this.log(`\n❌ Error — ver detalle abajo.`);
    } finally {
      this.loading = false;
    }
  }

  private log(msg: string) { this.logMessages.push(msg); }
}
